import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  Snackbar,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
const useDelete = (queryKey, token, refetchCallback) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [deleteMessage, setDeleteMessage] = useState("");
  const [deleteMessageOpen, setDeleteMessageOpen] = useState(false);
  const [deleteMessageSeverity, setDeleteMessageSeverity] = useState("success");

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      try {
        console.log('Deleting item with ID:', id);
        console.log('Delete URL:', `${process.env.REACT_APP_API_BASE_URL}/${queryKey}/${id}/`);
        const headers =
          auth?.buildAuthHeader?.() ||
          (token ? { Authorization: `Token ${token}` } : {});
        const response = await axios.delete(
          `${process.env.REACT_APP_API_BASE_URL}/${queryKey}/${id}/`,
          {
            headers,
          }
        );
        console.log('Delete response:', response);
      } catch (error) {
        console.error('Delete error:', error);
        throw new Error(error.response?.data?.detail || t("api.failedDelete"));
      }
    },
    onSuccess: () => {
      console.log('Delete successful, invalidating queries');
      setDeleteMessage(t("api.deleteSuccess"));
      setDeleteMessageSeverity("success");
      setDeleteMessageOpen(true);
      queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === queryKey.split('/')[1] });
      if (refetchCallback) refetchCallback();
      setTimeout(() => window.location.reload(), 1000);
    },
    onError: (error) => {
      console.error('Delete mutation error:', error);
      setDeleteMessage(error.message);
      setDeleteMessageSeverity("error");
      setDeleteMessageOpen(true);
    },
  });

  const handleDelete = (id) => {
    setItemToDelete(id);
    setConfirmDialogOpen(true);
  };

  const confirmDelete = () => {
    console.log('Confirm delete clicked for item:', itemToDelete);
    deleteMutation.mutate(itemToDelete);
    setConfirmDialogOpen(false);
  };

  useEffect(() => {
    if (deleteMessageOpen) {
      const timer = setTimeout(() => {
        setDeleteMessageOpen(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [deleteMessageOpen]);

  const DeleteMessageComponent = () => (
    <Snackbar
      open={deleteMessageOpen}
      autoHideDuration={3000}
      onClose={() => setDeleteMessageOpen(false)}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Alert
        onClose={() => setDeleteMessageOpen(false)}
        severity={deleteMessageSeverity}
        sx={{ width: '100%' }}
      >
        {deleteMessage}
      </Alert>
    </Snackbar>
  );

  const ConfirmDialog = ({ message = t("api.confirmMessage") }) => (
    <Dialog
      open={confirmDialogOpen}
      onClose={() => setConfirmDialogOpen(false)}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <WarningAmberIcon color="error" sx={{ fontSize: "5rem" }} />
          <Typography variant="h5" fontWeight={"bold"}>
            {t("api.confirmDelete")}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>{message}</DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={() => setConfirmDialogOpen(false)}
          color="primary"
          style={{margin:"0em 1em"}}
        >
          {t("api.cancel")}
        </Button>
        <Button variant="contained" onClick={confirmDelete} color="error">
          {t("api.delete")}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return { handleDelete, DeleteMessageComponent, ConfirmDialog };
};

export default useDelete;
